import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Modal, Button } from 'tea-component';
import { useIntl } from 'umi';
import TinyEditor from '@sg/components/TinyEditor';
import UploadFileList from '@sg/components/UploadFileList';
import ProjectInfo from '@sg/components/ProjectInfo';
import './index.scss';

export interface ITitleNoteFile {
  name: string;
  path: string;
  size?: string | number;
}

/**
 * 单条游戏 note 数据格式（与 utils.getFormatNotes / 接口返回保持一致）
 */
export interface ITitleNote {
  game_title: string;
  date?: string;
  avatar?: string;
  note: string;
  attach: ITitleNoteFile[];
}

interface IEditNotesModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  /** 月份标识，格式 "YYYY-MM"，用于弹窗标题 */
  month: string;
  /** 待编辑的 notes 列表（来自 hook 的 notes 状态） */
  notes: ITitleNote[];
  /** 父级 setNotes，用于在编辑时回写 notes 状态 */
  setNotes: React.Dispatch<React.SetStateAction<any[]>>;
  /** 上传附件的目录名 */
  uploadDomain?: string;
}

const EditNotesModal: React.FC<IEditNotesModalProps> = (props) => {
  const {
    visible,
    onClose,
    onConfirm,
    month,
    notes,
    setNotes,
    uploadDomain = 'mgmt/studio/live_titles/notes',
  } = props;

  const { formatMessage } = useIntl();
  const [submitting, setSubmitting] = useState(false);

  // 弹窗打开时对每个 note 的 content / attach 做一次快照，作为子组件
  // (TinyEditor / UploadFileList) 的稳定 defaultValue / defaultList 引用，
  // 避免编辑回写引发 default 引用变化 → 子组件 useEffect 再次 onResult →
  // 父 setState 死循环导致卡死（与 PlainText 中处理一致）
  const [initialSnapshot, setInitialSnapshot] = useState<
    Record<string, { content: string; files: ITitleNoteFile[] }>
  >({});

  // 缓存上一次 UploadFileList 回写的 attach 序列签名，用于去重，防止
  // onResult 触发同样数据时重复 setState 导致死循环
  const lastFilesSigRef = useRef<Record<string, string>>({});

  // attach 后端可能返回 JSON 字符串、数组、null/undefined，这里统一解析为数组
  const parseAttach = (raw: any): ITitleNoteFile[] => {
    if (Array.isArray(raw)) {
      return raw as ITitleNoteFile[];
    }
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as ITitleNoteFile[]) : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    const snapshot: Record<string, { content: string; files: ITitleNoteFile[] }> = {};
    let needNormalize = false;
    (notes || []).forEach((n: any) => {
      const key = n?.game_title;
      if (!key) {
        return;
      }
      const files = parseAttach(n?.attach);
      snapshot[key] = {
        content: n?.note || '',
        files,
      };
      // 父级 attach 仍是字符串/非数组时，需要回写一次以保证下游消费一致
      if (!Array.isArray(n?.attach)) {
        needNormalize = true;
      }
    });
    setInitialSnapshot(snapshot);
    lastFilesSigRef.current = {};
    if (needNormalize) {
      setNotes((prev: any[]) =>
        (prev || []).map((n: any) => ({
          ...n,
          attach: parseAttach(n?.attach),
        })),
      );
    }
  }, [visible]);

  const handleContentChange = (gameTitle: string, html: string) => {
    setNotes((prev) =>
      (prev || []).map((n: any) =>
        n?.game_title === gameTitle ? { ...n, note: html } : n,
      ),
    );
  };

  const handleFilesChange = (gameTitle: string, files: ITitleNoteFile[]) => {
    const safe = (files || []).map((f) => ({ name: f?.name, path: f?.path }));
    const sig = safe.map((f) => `${f.path}|${f.name}`).join(',');
    if (lastFilesSigRef.current[gameTitle] === sig) {
      return;
    }
    lastFilesSigRef.current[gameTitle] = sig;
    setNotes((prev) =>
      (prev || []).map((n: any) =>
        n?.game_title === gameTitle ? { ...n, attach: safe } : n,
      ),
    );
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await onConfirm();
    } catch (e) {
    } finally {
      setSubmitting(false);
    }
  };

  const caption = useMemo(() => `Notes (${month})`, [month]);

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      caption={caption}
      size="l"
      className="dbs-edit-notes-modal dbs-common-modal"
    >
      <Modal.Body>
        <div className="dbs-edit-notes-modal__list">
          {(notes || []).map((note: any, index: number) => {
            const key = note?.game_title || `note-${index}`;
            const snapshot = initialSnapshot[key] || { content: '', files: [] };
            return (
              <section key={key} className="dbs-edit-notes-modal__item">
                <ProjectInfo
                  name={note?.game_title}
                  src={note?.avatar}
                  width={36}
                />
                <div className="dbs-edit-notes-modal__editor dbs-rich-text-editor">
                  <TinyEditor
                    defaultValue={snapshot.content}
                    getEditorContent={(html: string) => handleContentChange(key, html)}
                    placeholder={formatMessage({ id: 'dbs_please_enter' })}
                    minHeight={120}
                    maxContentHeight={160}
                    isMaxHeightScroll={true}
                    isShowToolbar={true}
                  />
                </div>

                <div className="dbs-edit-notes-modal__upload dbs-plain-text-files">
                  <UploadFileList
                    className="dbs-styled-upload-file-list"
                    uniqueId={`edit-notes-${index}`}
                    uploadFolderName={uploadDomain}
                    defaultList={snapshot.files as any}
                    onResult={(files: ITitleNoteFile[]) => handleFilesChange(key, files)}
                    showFileList={true}
                    multiple={true}
                    fileListPosition="right"
                    buttonTips=""
                    autoResizeInput={true}
                    includeSizeInResult={false}
                  />
                </div>
              </section>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            handleConfirm();
          }}
          disabled={submitting}
          className="dbs-gradient-btn"
        >
          {formatMessage({ id: 'dbs_confirm' })}
        </Button>
        <Button onClick={onClose}>
          {formatMessage({ id: 'dbs_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditNotesModal;
